export const TIER_ORDER = [
  "Junior Mini (Pickleball)",
  "Junior Beginner",
  "Junior Intermediate",
  "Junior Advanced",
  "Adult Beginner",
  "Adult Intermediate",
  "Adult Adv Intermediate",
  "Adult Advanced / Elite",
  "No Group",
];

export const TIER_COLORS = {
  "Junior Mini (Pickleball)": { bg: "bg-purple-50", header: "bg-purple-200", text: "text-purple-800", border: "border-purple-200" },
  "Junior Beginner":          { bg: "bg-green-50",  header: "bg-green-200",  text: "text-green-800",  border: "border-green-200" },
  "Junior Intermediate":      { bg: "bg-teal-50",   header: "bg-teal-200",   text: "text-teal-800",   border: "border-teal-200" },
  "Junior Advanced":          { bg: "bg-cyan-50",   header: "bg-cyan-200",   text: "text-cyan-800",   border: "border-cyan-200" },
  "Adult Beginner":           { bg: "bg-yellow-50", header: "bg-yellow-200", text: "text-yellow-800", border: "border-yellow-200" },
  "Adult Intermediate":       { bg: "bg-orange-50", header: "bg-orange-200", text: "text-orange-800", border: "border-orange-200" },
  "Adult Adv Intermediate":   { bg: "bg-red-50",    header: "bg-red-200",    text: "text-red-800",    border: "border-red-200" },
  "Adult Advanced / Elite":   { bg: "bg-rose-50",   header: "bg-rose-200",   text: "text-rose-800",   border: "border-rose-200" },
  "No Group":                 { bg: "bg-gray-50",   header: "bg-gray-200",   text: "text-gray-700",   border: "border-gray-200" },
  // Weekend session "tiers" — not part of TIER_ORDER (so they stay out of the
  // weekday instruction stats/pickers), only used for grid coloring.
  "Open Play":                { bg: "bg-sky-50",    header: "bg-sky-200",    text: "text-sky-800",    border: "border-sky-300" },
  "Private":                  { bg: "bg-indigo-50", header: "bg-indigo-200", text: "text-indigo-800", border: "border-indigo-300" },
};

// Weekday instruction days. Kept to Mon–Fri so the group scheduler, auto-
// organize, match-finder, and availability forms are unaffected by weekends.
export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Weekends run a different program: open play in the mornings, private
// lessons from the afternoon on. These are additive — only the week-view grid,
// coach schedules, and per-day stats iterate the full 7 days.
export const WEEKEND_DAYS = ["Saturday", "Sunday"];
export const ALL_DAYS = [...DAYS, ...WEEKEND_DAYS];

// The soft divider between the open-play morning and the private-lesson
// afternoon on weekends (informational, not enforced).
export const WEEKEND_SPLIT_TIME = "1:00 PM";

// Selectable session lengths (hours). Privates can be shorter; open play runs
// in the same 1/1.5/2-hour blocks as tennis groups.
export const PRIVATE_DURATION_OPTIONS = [0.5, 1, 1.5];
export const OPEN_PLAY_DURATION_OPTIONS = [1, 1.5, 2];

// True for a normal weekday instruction group (everything that isn't a
// weekend open-play or private session). Used to keep weekend sessions out of
// instruction-only views and stats.
export function isInstructionGroup(g) {
  return !g.kind;
}

// Schedule runs 6:00 AM–10:00 PM in 1-hour blocks.
const SCHEDULE_START_HOUR = 6;
const SCHEDULE_END_HOUR = 22;

function timeLabel(totalMinutes) {
  let h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const ap = h < 12 || h === 24 ? "AM" : "PM";
  let displayH = h % 12;
  if (displayH === 0) displayH = 12;
  return `${displayH}:${String(m).padStart(2, "0")} ${ap}`;
}

function hourLabel(h) {
  return timeLabel(h * 60);
}

export const TIME_SLOTS = Array.from(
  { length: SCHEDULE_END_HOUR - SCHEDULE_START_HOUR },
  (_, i) => `${hourLabel(SCHEDULE_START_HOUR + i)}–${hourLabel(SCHEDULE_START_HOUR + i + 1)}`
);

// Half-hour grid rows used by the Weekly Grid so a 1.5-hour session can span
// exactly 3 rows instead of rounding up to a full extra hour.
export const HALF_SLOTS = Array.from(
  { length: (SCHEDULE_END_HOUR - SCHEDULE_START_HOUR) * 2 },
  (_, i) => timeLabel(SCHEDULE_START_HOUR * 60 + i * 30)
);

// Selectable start times for a group's schedule: every half hour, so any
// group can start on the hour or the half hour (not just the fixed 3:30 PM
// junior after-school session).
export const TIME_START_OPTIONS = HALF_SLOTS;

export const SCHEDULE_CLOSE_MINUTES = SCHEDULE_END_HOUR * 60;

// The two fixed after-school session start times for kids: 3:30 PM and
// 4:30 PM. Every junior session — Tennis or Pickleball — is always exactly
// 1 hour long.
export const JUNIOR_SESSIONS = [
  { startTime: "3:30 PM", duration: 1 },
  { startTime: "4:30 PM", duration: 1 },
];

// The whole-hour TIME_SLOTS buckets a junior group may sit in — used for the
// general "is this junior group inside eligible hours" warning check and for
// the slots a junior sees on the sign-up form. Wider than the two fixed
// sessions above: juniors may also be scheduled up to 7:00 PM.
export const JUNIOR_ELIGIBLE_SLOTS = [
  "3:00 PM–4:00 PM",
  "4:00 PM–5:00 PM",
  "5:00 PM–6:00 PM",
  "6:00 PM–7:00 PM",
];

// The club's two tennis courts are known by where they sit, not by number.
// The pickleball courts keep theirs — and courtTypeOf() below reads the sport
// off that prefix, so any court whose name doesn't start "Pickleball" counts
// as tennis.
export const COURTS = [
  "Upper Court",
  "Lower Court",
  "Pickleball Court 1",
  "Pickleball Court 2",
];

// What the tennis courts were called before they were Upper and Lower. The
// saved schedule still carries these until it is next written, and a group on
// a court the grid has no column for simply vanishes from it.
const LEGACY_COURT_NAMES = {
  "Tennis Court 1": "Upper Court",
  "Tennis Court 2": "Lower Court",
};

export function renameLegacyCourts(groups) {
  return (groups || []).map(g =>
    LEGACY_COURT_NAMES[g.court] ? { ...g, court: LEGACY_COURT_NAMES[g.court] } : g
  );
}

export function courtTypeOf(court) {
  return court && court.startsWith("Pickleball") ? "Pickleball" : "Tennis";
}
